import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	Search,
	Loader2,
	LayoutGrid,
	List as ListIcon,
	Filter,
	Settings2,
	Building2,
	UserCheck,
	GripVertical
} from 'lucide-react';
import {
	DndContext,
	PointerSensor,
	KeyboardSensor,
	useSensor,
	useSensors,
	useDraggable,
	useDroppable,
	DragOverlay,
	pointerWithin,
	type DragEndEvent,
	type DragStartEvent
} from '@dnd-kit/core';
import {
	Badge,
	Button,
	Card,
	CardContent,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Textarea,
	useToast
} from '@hiring-intelligence/ui';
import {
	DEAL_STAGES,
	DEAL_STAGE_OPTIONS,
	DEAL_STATUS_OPTIONS,
	PRIORITY_OPTIONS,
	DEAL_WON_STAGE,
	DEAL_LOST_STAGE,
	formatNumber
} from '@hiring-intelligence/shared';
import { differenceInDays } from 'date-fns';
import { crmDealsApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import {
	ListPageShell,
	FilterChipsBar,
	ListToolbarRow,
	CrmDataTable,
	NameCell,
	MetricsRow,
	type Column,
	type ChipDef,
	type MetricCellData
} from '@/components/crm/layout';

const stageLabel = (v: string) => DEAL_STAGE_OPTIONS.find((o) => o.value === v)?.label || v;
const PAGE_LIMIT = 25;

const STAGE_PROBABILITY: Record<string, number> = {
	lead: 0.1, qualified: 0.25, proposal: 0.4, interviewing: 0.6, offer: 0.8, placed: 1, lost: 0
};

const PRIORITY_BADGE: Record<string, string> = {
	low: 'bg-slate-100 text-slate-700',
	medium: 'bg-amber-100 text-amber-700',
	high: 'bg-red-100 text-red-700'
};

const STAGE_ACCENT: Record<string, string> = {
	lead: 'border-t-slate-400',
	qualified: 'border-t-blue-400',
	proposal: 'border-t-indigo-400',
	interviewing: 'border-t-purple-400',
	offer: 'border-t-amber-400',
	placed: 'border-t-emerald-500',
	lost: 'border-t-red-400'
};

const SAVED_VIEWS: { chip: ChipDef; predicate?: (d: any, currentUserId?: string) => boolean }[] = [
	{ chip: { value: 'all', label: 'All deals' } },
	{ chip: { value: 'mine', label: 'My deals' }, predicate: (d, uid) => d.ownerId === uid },
	{ chip: { value: 'open', label: 'Open' }, predicate: (d) => d.status === 'open' },
	{ chip: { value: 'won', label: 'Closed Won' }, predicate: (d) => d.stage === DEAL_WON_STAGE }
];

function dealAmount(d: any): number {
	const min = d.salaryRange?.min || 0;
	const max = d.salaryRange?.max || 0;
	const mid = (min && max) ? (min + max) / 2 : (min || max || 0);
	const fee = d.feePercentage ? d.feePercentage / 100 : 0;
	return Math.round(mid * fee);
}

function formatIDR(n: number): string {
	if (!Number.isFinite(n) || n === 0) return 'IDR 0';
	if (n >= 1e9) return `IDR ${(n / 1e9).toFixed(2)}B`;
	if (n >= 1e6) return `IDR ${(n / 1e6).toFixed(2)}M`;
	if (n >= 1e3) return `IDR ${(n / 1e3).toFixed(1)}K`;
	return `IDR ${formatNumber(n)}`;
}

function formatSalary(sr?: { min?: number; max?: number; currency?: string } | null): string | null {
	if (!sr || (!sr.min && !sr.max)) return null;
	const cur = sr.currency || 'IDR';
	const min = sr.min ? formatNumber(sr.min) : '';
	const max = sr.max ? formatNumber(sr.max) : '';
	if (min && max) return `${cur} ${min}–${max}`;
	return `${cur} ${min || max}`;
}

// ---- Kanban pieces ----

function DealCard({ deal, dragging, myId, onAssignToMe }: { deal: any; dragging?: boolean; myId?: string; onAssignToMe?: (id: string) => void }) {
	const salary = formatSalary(deal.salaryRange);
	return (
		<Card className={`cursor-pointer hover:shadow-md transition-shadow ${dragging ? 'shadow-lg ring-2 ring-primary' : ''}`}>
			<CardContent className="p-3 space-y-2">
				<div className="flex items-start gap-2">
					<GripVertical className="h-4 w-4 text-muted-foreground/40 shrink-0 mt-0.5 cursor-grab active:cursor-grabbing" />
					<div className="min-w-0 flex-1">
						<p className="text-sm font-medium leading-tight">{deal.title}</p>
						{deal.roleTitle && <p className="text-xs text-muted-foreground truncate mt-0.5">{deal.roleTitle}</p>}
					</div>
				</div>
				{deal.company?.name && (
					<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
						<Building2 className="h-3 w-3 shrink-0" />
						<span className="truncate">{deal.company.name}</span>
					</div>
				)}
				{salary && <p className="text-xs text-muted-foreground">{salary}</p>}
				<div className="flex items-center justify-between gap-2 pt-1">
					{deal.priority ? (
						<span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PRIORITY_BADGE[deal.priority] || ''}`}>
							{PRIORITY_OPTIONS.find((o) => o.value === deal.priority)?.label}
						</span>
					) : <span />}
					<div className="flex items-center gap-1.5 min-w-0">
						{deal.owner?.name && <span className="text-[10px] text-muted-foreground truncate">{deal.owner.name}</span>}
						{myId && deal.ownerId !== myId && onAssignToMe && (
							<button
								type="button"
								onClick={(e) => { e.stopPropagation(); onAssignToMe(deal.id); }}
								className="inline-flex items-center gap-0.5 text-[10px] text-primary hover:underline shrink-0"
								title="Assign to me"
							>
								<UserCheck className="h-3 w-3" /> Assign
							</button>
						)}
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function DraggableDealCard({ deal, onClick, myId, onAssignToMe }: { deal: any; onClick: () => void; myId?: string; onAssignToMe?: (id: string) => void }) {
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: deal.id, data: { stage: deal.stage } });
	return (
		<div ref={setNodeRef} {...attributes} {...listeners} onClick={onClick} className={isDragging ? 'opacity-40' : ''}>
			<DealCard deal={deal} myId={myId} onAssignToMe={onAssignToMe} />
		</div>
	);
}

function KanbanColumn({ stage, deals, onCardClick, myId, onAssignToMe }: { stage: string; deals: any[]; onCardClick: (id: string) => void; myId?: string; onAssignToMe?: (id: string) => void }) {
	const { setNodeRef, isOver } = useDroppable({ id: stage });
	const totalAmount = deals.reduce((s, d) => s + dealAmount(d), 0);
	return (
		<div className="flex w-72 shrink-0 flex-col">
			<div className={`flex items-center justify-between px-3 py-2 border-t-4 rounded-t bg-card ${STAGE_ACCENT[stage] || 'border-t-slate-300'}`}>
				<div>
					<div className="text-sm font-semibold">{stageLabel(stage)}</div>
					<div className="text-[10px] text-muted-foreground">{deals.length} · {formatIDR(totalAmount)}</div>
				</div>
				<Badge variant="secondary" className="text-xs">{deals.length}</Badge>
			</div>
			<div
				ref={setNodeRef}
				className={`flex-1 space-y-2 p-2 rounded-b-md min-h-[200px] transition-colors ${isOver ? 'bg-primary/5 ring-2 ring-primary/30' : 'bg-muted/20'}`}
			>
				{deals.map((deal) => (
					<DraggableDealCard key={deal.id} deal={deal} onClick={() => onCardClick(deal.id)} myId={myId} onAssignToMe={onAssignToMe} />
				))}
				{deals.length === 0 && (
					<p className="text-xs text-muted-foreground/60 text-center py-4">No deals</p>
				)}
			</div>
		</div>
	);
}

// ---- Page ----

export default function DealsPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const currentUser = useAuthStore((s) => s.user);
	const myId = currentUser?.id;

	const [view, setView] = useState<'kanban' | 'list'>('kanban');
	const [savedView, setSavedView] = useState('all');
	const [search, setSearch] = useState('');
	const [stageFilter, setStageFilter] = useState('all');
	const [statusFilter, setStatusFilter] = useState('all');
	const [page, setPage] = useState(1);
	const [activeId, setActiveId] = useState<string | null>(null);

	const [placeConfirm, setPlaceConfirm] = useState<{ dealId: string; title: string } | null>(null);
	const [loseConfirm, setLoseConfirm] = useState<{ dealId: string; title: string } | null>(null);
	const [lostReason, setLostReason] = useState('');

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
		useSensor(KeyboardSensor)
	);

	const { data: boardData, isLoading: boardLoading } = useQuery({
		queryKey: ['crm-deals-board'],
		queryFn: () => crmDealsApi.board(),
		enabled: view === 'kanban'
	});
	const { data: listData, isLoading: listLoading } = useQuery({
		queryKey: ['crm-deals', { page, q: search, stage: stageFilter, status: statusFilter, savedView, myId }],
		queryFn: () => crmDealsApi.list({
			page, limit: PAGE_LIMIT,
			q: search || undefined,
			stage: stageFilter === 'all' ? (savedView === 'won' ? DEAL_WON_STAGE : undefined) : stageFilter,
			status: statusFilter === 'all' ? (savedView === 'open' ? 'open' : undefined) : statusFilter,
			ownerId: savedView === 'mine' ? myId : undefined
		}),
		enabled: view === 'list'
	});

	const invalidate = () => {
		queryClient.invalidateQueries({ queryKey: ['crm-deals-board'] });
		queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
	};

	const stageMutation = useMutation({
		mutationFn: ({ id, stage }: { id: string; stage: string }) => crmDealsApi.updateStage(id, stage),
		onSuccess: () => { invalidate(); toast({ title: 'Stage updated' }); },
		onError: (e: Error) => { invalidate(); toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
	});

	const loseMutation = useMutation({
		mutationFn: ({ id, reason }: { id: string; reason?: string }) => crmDealsApi.lose(id, reason),
		onSuccess: () => { invalidate(); setLoseConfirm(null); setLostReason(''); toast({ title: 'Marked as lost' }); },
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const assignMutation = useMutation({
		mutationFn: (dealId: string) => crmDealsApi.update(dealId, { ownerId: myId }),
		onSuccess: () => { invalidate(); toast({ title: 'Assigned to you' }); },
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const boardDeals: any[] = boardData?.data || [];

	// Saved-view filter applied to kanban
	const viewFiltered = useMemo(() => {
		const def = SAVED_VIEWS.find((v) => v.chip.value === savedView);
		if (!def?.predicate) return boardDeals;
		return boardDeals.filter((d) => def.predicate!(d, myId));
	}, [boardDeals, savedView, myId]);

	const grouped = useMemo(() => {
		const map: Record<string, any[]> = {};
		for (const stage of DEAL_STAGES) map[stage] = [];
		for (const d of viewFiltered) {
			const s = String(d.stage);
			(map[s] ||= []).push(d);
		}
		return map;
	}, [viewFiltered]);

	// Metrics computed from board data (all visible deals)
	const metrics: MetricCellData[] = useMemo(() => {
		const total = viewFiltered.reduce((s, d) => s + dealAmount(d), 0);
		const weighted = viewFiltered.reduce((s, d) => s + dealAmount(d) * (STAGE_PROBABILITY[d.stage] ?? 0), 0);
		const open = viewFiltered.filter((d) => d.status === 'open').reduce((s, d) => s + dealAmount(d), 0);
		const closedWon = viewFiltered.filter((d) => d.stage === DEAL_WON_STAGE).reduce((s, d) => s + dealAmount(d), 0);
		const now = new Date();
		const newThisMonth = viewFiltered.filter((d) => {
			const created = d.createdAt ? new Date(d.createdAt) : null;
			return created && created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
		}).reduce((s, d) => s + dealAmount(d), 0);
		const openDeals = viewFiltered.filter((d) => d.status === 'open');
		const avgAge = openDeals.length === 0 ? 0 : Math.round(openDeals.reduce((s, d) => s + (d.createdAt ? differenceInDays(now, new Date(d.createdAt)) : 0), 0) / openDeals.length);
		const perDeal = (n: number) => viewFiltered.length === 0 ? 0 : Math.round(n / viewFiltered.length);
		return [
			{ label: 'Total Deal Amount', value: formatIDR(total), hint: `Average ${formatIDR(perDeal(total))} per deal` },
			{ label: 'Weighted Deal Amount', value: formatIDR(weighted), hint: `Average ${formatIDR(perDeal(weighted))} per deal` },
			{ label: 'Open Deal Amount', value: formatIDR(open), tone: 'default', hint: `${viewFiltered.filter((d) => d.status === 'open').length} deals` },
			{ label: 'Closed Won Amount', value: formatIDR(closedWon), tone: 'success', hint: `${viewFiltered.filter((d) => d.stage === DEAL_WON_STAGE).length} won` },
			{ label: 'New This Month', value: formatIDR(newThisMonth), tone: 'muted' },
			{ label: 'Average Deal Age', value: `${avgAge}d`, tone: 'muted', hint: 'open deals' }
		];
	}, [viewFiltered]);

	const activeDeal = activeId ? viewFiltered.find((d: any) => d.id === activeId) : null;
	const handleDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
	const handleDragEnd = (e: DragEndEvent) => {
		setActiveId(null);
		const { active, over } = e;
		if (!over) return;
		const dealId = String(active.id);
		const fromStage = String(active.data.current?.stage);
		const toStage = String(over.id);
		if (fromStage === toStage) return;
		if (toStage === DEAL_WON_STAGE) {
			const d = viewFiltered.find((x: any) => x.id === dealId);
			setPlaceConfirm({ dealId, title: d?.title || 'this deal' });
			return;
		}
		if (toStage === DEAL_LOST_STAGE) {
			const d = viewFiltered.find((x: any) => x.id === dealId);
			setLoseConfirm({ dealId, title: d?.title || 'this deal' });
			return;
		}
		stageMutation.mutate({ id: dealId, stage: toStage });
	};

	const listRows: any[] = listData?.data || [];
	const listMeta = listData?.meta;

	const columns: Column<any>[] = [
		{
			key: 'name',
			header: 'Deal Name',
			cell: (d) => <NameCell name={d.title} secondary={d.roleTitle} />
		},
		{
			key: 'stage',
			header: 'Deal Stage',
			cell: (d) => <Badge variant="secondary">{stageLabel(String(d.stage))}</Badge>
		},
		{
			key: 'company',
			header: 'Company',
			cell: (d) => d.company?.name ? (
				<a href={`/companies/${d.companyId}`} onClick={(e) => e.stopPropagation()} className="text-primary hover:underline">{d.company.name}</a>
			) : <span className="text-muted-foreground">—</span>
		},
		{
			key: 'amount',
			header: 'Amount',
			cell: (d) => <span className="font-medium tabular-nums">{formatIDR(dealAmount(d))}</span>
		},
		{
			key: 'owner',
			header: 'Deal Owner',
			cell: (d) => (
				<div className="flex items-center gap-2">
					{d.owner?.name ? <span>{d.owner.name}</span> : <span className="text-muted-foreground">—</span>}
					{myId && d.ownerId !== myId && (
						<Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={(e) => { e.stopPropagation(); assignMutation.mutate(d.id); }}>
							<UserCheck className="mr-1 h-3 w-3" /> Assign to me
						</Button>
					)}
				</div>
			)
		},
		{
			key: 'createdAt',
			header: 'Create Date',
			cell: (d) => <span className="text-muted-foreground">{d.createdAt ? new Date(d.createdAt).toLocaleDateString() : '—'}</span>
		}
	];

	return (
		<>
			<ListPageShell
				title="Deals"
				subtitle="Pipeline of opportunities being worked across the agency."
				recordCount={view === 'list' ? listMeta?.total : viewFiltered.length}
				primaryActions={
					<>
						<div className="inline-flex rounded-md border p-0.5">
							<Button variant={view === 'kanban' ? 'default' : 'ghost'} size="sm" onClick={() => setView('kanban')} className="gap-1.5">
								<LayoutGrid className="h-4 w-4" /> Kanban
							</Button>
							<Button variant={view === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setView('list')} className="gap-1.5">
								<ListIcon className="h-4 w-4" /> List
							</Button>
						</div>
					</>
				}
				chips={
					<FilterChipsBar
						chips={SAVED_VIEWS.map((v) => v.chip)}
						activeValue={savedView}
						onSelect={setSavedView}
						totalViewsLabel={`(${SAVED_VIEWS.length}/50)`}
					/>
				}
				metrics={<MetricsRow metrics={metrics} />}
				toolbar={view === 'list' ? (
					<ListToolbarRow>
						<div className="relative w-full max-w-sm">
							<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
							<Input placeholder="Search deals…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="h-9 pl-8 text-sm" />
						</div>
						<Select value={stageFilter} onValueChange={(v) => { setStageFilter(v); setPage(1); }}>
							<SelectTrigger className="h-9 w-[160px] text-sm"><SelectValue placeholder="Stage" /></SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All stages</SelectItem>
								{DEAL_STAGE_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
							</SelectContent>
						</Select>
						<Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
							<SelectTrigger className="h-9 w-[160px] text-sm"><SelectValue placeholder="Status" /></SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All statuses</SelectItem>
								{DEAL_STATUS_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
							</SelectContent>
						</Select>
						<Button variant="outline" size="sm" disabled><Filter className="mr-1.5 h-3.5 w-3.5" /> Advanced filters</Button>
						<div className="ml-auto flex items-center gap-2">
							<Button variant="ghost" size="sm" disabled className="text-xs">Export</Button>
							<Button variant="ghost" size="sm" disabled className="text-xs"><Settings2 className="mr-1 h-3.5 w-3.5" /> Edit columns</Button>
						</div>
					</ListToolbarRow>
				) : null}
			>
				{view === 'kanban' ? (
					boardLoading ? (
						<div className="flex items-center justify-center gap-2 py-12 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
					) : (
						<DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
							<div className="flex gap-3 overflow-x-auto p-3">
								{DEAL_STAGES.map((stage) => (
									<KanbanColumn key={stage} stage={stage} deals={grouped[stage] || []} onCardClick={(id) => navigate(`/deals/${id}`)} myId={myId} onAssignToMe={(id) => assignMutation.mutate(id)} />
								))}
							</div>
							<DragOverlay>{activeDeal ? <DealCard deal={activeDeal} dragging /> : null}</DragOverlay>
						</DndContext>
					)
				) : (
					<CrmDataTable
						columns={columns}
						rows={listRows}
						loading={listLoading}
						onRowClick={(d) => navigate(`/deals/${d.id}`)}
						empty={<div className="text-muted-foreground">No deals match. Deals are created automatically with a hiring request. <button onClick={() => navigate('/hiring-requests')} className="text-primary hover:underline">New hiring request →</button></div>}
						footer={listMeta && listMeta.totalPages > 1 ? (
							<div className="flex items-center justify-between">
								<span>Page {page} of {listMeta.totalPages}</span>
								<div className="flex gap-2">
									<Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Prev</Button>
									<Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={!listMeta.hasMore}>Next</Button>
								</div>
							</div>
						) : undefined}
					/>
				)}
			</ListPageShell>

			{/* Place (won) confirm */}
			<Dialog open={!!placeConfirm} onOpenChange={(o) => { if (!o) setPlaceConfirm(null); }}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Mark deal as Placed?</DialogTitle>
						<DialogDescription>
							Moving "{placeConfirm?.title}" to Placed will mark it as won. Open the deal and use "Add Hiring Request" when you're ready to start the recruitment work.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={() => setPlaceConfirm(null)}>Cancel</Button>
						<Button onClick={() => {
							if (placeConfirm) stageMutation.mutate({ id: placeConfirm.dealId, stage: DEAL_WON_STAGE });
							setPlaceConfirm(null);
						}} disabled={stageMutation.isPending}>
							{stageMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm Won
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Lose confirm */}
			<Dialog open={!!loseConfirm} onOpenChange={(o) => { if (!o) { setLoseConfirm(null); setLostReason(''); } }}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Mark deal as Lost?</DialogTitle>
						<DialogDescription>You can add an optional reason.</DialogDescription>
					</DialogHeader>
					<div className="grid gap-2 py-2">
						<Label htmlFor="lost-reason">Lost reason (optional)</Label>
						<Textarea id="lost-reason" placeholder="e.g. Client chose another agency…" rows={3} value={lostReason} onChange={(e) => setLostReason(e.target.value)} />
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => { setLoseConfirm(null); setLostReason(''); }}>Cancel</Button>
						<Button variant="destructive" onClick={() => { if (loseConfirm) loseMutation.mutate({ id: loseConfirm.dealId, reason: lostReason.trim() || undefined }); }} disabled={loseMutation.isPending}>
							{loseMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Mark as Lost
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
